import "./timepicker.scss";
import React from "react";
import moment, { type Moment } from "moment";
import FontAwesomeLight from "../../../Utilities/Platform/FontAwesome/FontAwesomeLight";
import TimePickerTrack from "./TimePickerTrack";

type Props = {
    mobile: boolean;
    range: boolean;
    start: Moment | null;
    end: Moment | null;
    onChange: (name: string, value: Moment) => void;
};

type TimePickerValuesType = {
    hours: number;
    minutes: number;
    seconds: number;
};

type State = {
    start: TimePickerValuesType;
    end: TimePickerValuesType;
};

function parseValues(value: Moment | null, end = false): TimePickerValuesType {
    const results = {
        hours  : (end ? 23 : 0),
        minutes: (end ? 59 : 0),
        seconds: (end ? 59 : 0),
    };

    if(moment.isMoment(value)) {
        results.hours = value.hours();
        results.minutes = value.minutes();
        results.seconds = value.seconds();
    }

    return results;
}

export default class TimePicker extends React.Component<Props, State> {
    constructor(props: Readonly<Props>) {
        super(props);

        this.state = {
            start: parseValues(props.start),
            end  : parseValues(props.end, true),
        };
    }

    public componentDidUpdate(prevProps: Readonly<Props>): void {
        if(prevProps.start !== this.props.start) {
            this.setState({
                start: parseValues(this.props.start),
            });
        }

        if(prevProps.end !== this.props.end) {
            this.setState({
                end: parseValues(this.props.end, true),
            });
        }
    }

    public handleStartChange = (name: string, value: number): void => {
        this.setState(prevState => ({
            start: {
                ...prevState.start,
                [name]: value,
            },
        }), () => {
            let start = moment();

            if(moment.isMoment(this.props.start)) {
                start = this.props.start.clone();
            }

            start.hours(this.state.start.hours)
                .minutes(this.state.start.minutes)
                .seconds(this.state.start.seconds);

            this.props.onChange("start", start);
        });
    };

    public handleEndChange = (name: string, value: number): void => {
        this.setState(prevState => ({
            end: {
                ...prevState.end,
                [name]: value,
            },
        }), () => {
            let end = moment();

            if(moment.isMoment(this.props.end)) {
                end = this.props.end.clone();
            }

            end.hours(this.state.end.hours)
                .minutes(this.state.end.minutes)
                .seconds(this.state.end.seconds);

            this.props.onChange("end", end);
        });
    };

    public render(): React.ReactNode {
        return (
            <div className={"calendar-time-picker" + (this.props.range ? " range" : "")}>
                <div className="calendar-time-picker-wrapper">
                    <TimePickerTrack
                        min={0}
                        max={23}
                        value={this.state.start.hours}
                        name="hours"
                        onChange={this.handleStartChange}
                    />
                    <div className="colon-separator">:</div>
                    <TimePickerTrack
                        min={0}
                        max={59}
                        value={this.state.start.minutes}
                        name="minutes"
                        onChange={this.handleStartChange}
                    />
                    <div className="colon-separator">:</div>
                    <TimePickerTrack
                        min={0}
                        max={59}
                        value={this.state.start.seconds}
                        name="seconds"
                        onChange={this.handleStartChange}
                    />
                </div>
                {this.props.range && (
                    <React.Fragment>
                        <FontAwesomeLight icon="minus" />
                        <div className="calendar-time-picker-wrapper">
                            <TimePickerTrack
                                min={0}
                                max={23}
                                value={this.state.end.hours}
                                name="hours"
                                onChange={this.handleEndChange}
                            />
                            <div className="colon-separator">:</div>
                            <TimePickerTrack
                                min={0}
                                max={59}
                                value={this.state.end.minutes}
                                name="minutes"
                                onChange={this.handleEndChange}
                            />
                            <div className="colon-separator">:</div>
                            <TimePickerTrack
                                min={0}
                                max={59}
                                value={this.state.end.seconds}
                                name="seconds"
                                onChange={this.handleEndChange}
                            />
                        </div>
                    </React.Fragment>
                )}
            </div>
        );
    }
}
